import React from 'react'
import { PageProps } from 'gatsby' //, Link, graphql

import cn from 'classnames'

import SEO from '~/components/seo'
import Kv from '~/components/sub-kv'

import DeptIcon1 from '~/assets/svg/q-k1i.svg'
import DeptIcon2 from '~/assets/svg/q-k2i.svg'

import ShiftLayerQ from '~/assets/svg/q-k1.svg'
import ShiftLayerLargeImage from '~/assets/svg/v3/kan-4.svg'
import ShiftLayerLargeImageSp from '~/assets/svg/v3/kan-4-sp.svg'

import Arrow from '~/assets/svg/p-arrow-right.svg'

import s from './styles.module.scss'
import getPrefixedPath from '~/utils/getPrefixedPath'
import Tilt from '~/components/tilt'
import Shift from '~/components/shift'

type DataProps = {}

const Page: React.FC<PageProps<DataProps>> = ({}) => (
  <>
    <SEO
      title="環境システム学科のフィールドワーク紹介"
      description="環境システム学科で実際に行われているフィールドワークの一例をご紹介します。荒川河川敷の草原で行われている火入れがなぜ希少植物の保護につながるのかについて調べ、考えました。"
    />

    <Kv
      title={
        <>
          草原を燃やして、
          <br />
          絶滅危惧種を守る。
        </>
      }
      imgPc="/assets/images/pc/p-pc-k3-1.jpg"
      imgSp="/assets/images/sp/p-sp-k3-1.jpg"
    />

    <section className={cn(s.content, s.es)}>
      <div className={s.text}>
        <div className={s.flex}>
          <div className={s.left}>
            <div className={s.step}>step1</div>
            <h2>火入れが植物を救う？</h2>
            <p>
              ハリエンジュの林になってしまうのを防いで希少植物を保護するために，荒川の河川敷で火入れが行われたことが新聞に書いてありました。しかし、生物を焼き殺してしまいそうな火入れがなぜ希少植物の保護につながるのか、せっかく林になりかけているのになぜ燃やしてしまうのか、CO
              <sub>2</sub>
              濃度を高めてしまって地球環境には良くないのではないかと疑問に思いました。そこで、火入れをすると希少植物が増えたり、減らなかったりするのか、また、ハリエンジュの林がなぜ悪者にされるのかを調べることにしました。
            </p>
          </div>
          <div className={cn(s.right)}>
            <Shift
              className={s.shiftEs1}
              layer={[
                <div className={s.layer1}>
                  <ShiftLayerQ />
                </div>,
                <div className={s.layer2}>
                  <Tilt direction="right">
                    <img
                      src={getPrefixedPath(
                        '/assets/images/pc/v4/p-half-kan-b1.jpg'
                      )}
                    />
                  </Tilt>
                  <p className={s.caption}>ハリエンジュ若齢林</p>
                </div>,
              ]}
            />
          </div>
        </div>

        <div className={s.flex}>
          <div className={s.left}>
            <div className={s.step}>step2</div>
            <h2>
              現地へ行き、
              <br />
              事実を知ること。
            </h2>
            <p>
              荒川大麻生公園では植物の枯れた冬に、少しずつ場所を変えながら、ほぼ毎年火入れが行われています。そのため、火入れをした場所としなかった場所、過去の火入れ回数が多い場所と少ない場所などが隣り合って混在しています。そのような場所に生育している植物を比較すると、火入れの有無や回数が植物の生育に及ぼす効果を調べることができます。そのため、大麻生公園は火入れの影響を調べるのに適した、めったにない場所なのです。
              <br />
              大麻生公園付近ではカワラナデシコ、イヌハギ、カワラサイコ、カワラヨモギ、ハタザオなどの絶滅危惧種が生育している一方、外来樹木のハリエンジュ（別名ニセアカシア）やニワウルシの樹林が拡大する恐れがあります。そこで、これらの希少植物や外来樹木が火入れを行うと増えるのか、減るのかを、火入れをしなかった場所と比較して明らかにしていきます。具体的には植物が大きくなったか、タネやイヌハギの芽生えの生き残り数の変化から子孫が増えるかを現地で調べます。発芽試験など室内実験が必要な場合もありますが、基本はフィールドで大きさを測り、数を数えることです。
            </p>
          </div>

          <div className={cn(s.right)}>
            <div className={s.paddingTop}>
              <img
                src={getPrefixedPath('/assets/images/pc/v4/p-half-kan-b2.jpg')}
              />
              <p className={cn(s.caption)}>荒川調査風景</p>
              <img
                src={getPrefixedPath('/assets/images/pc/v4/p-half-kan-b3.jpg')}
              />
              <p className={s.caption}>
                火入れをした場所（右）としなかった場所（左）における春の芽生えの比較
              </p>
            </div>
          </div>
        </div>

        <div className={cn(s.images, s.flex)}>
          <div className={cn(s.left, s.top, s.flex)}>
            <div className={cn(s.left, s.top)}>
              <img src={getPrefixedPath('/assets/images/pc/v4/kan-c1.jpg')} />
              <p className={s.caption}>イヌハギ</p>
            </div>
            <div className={cn(s.right, s.top)}>
              <img src={getPrefixedPath('/assets/images/pc/v4/kan-c2.jpg')} />
              <p className={s.caption}>ハリエンジュ</p>
            </div>
          </div>
          <div className={cn(s.right, s.top, s.flex)}>
            <div className={cn(s.left, s.top)}>
              <img src={getPrefixedPath('/assets/images/pc/v4/kan-c3.jpg')} />
              <p className={s.caption}>カワラナデシコ</p>
            </div>
            <div className={cn(s.right, s.top)}>
              <img src={getPrefixedPath('/assets/images/pc/v4/kan-c4.jpg')} />
              <p className={s.caption}>カワラサイコ群落</p>
            </div>
          </div>
        </div>

        <div className={s.flex}>
          <div className={s.left}>
            <div className={s.step}>step3</div>
            <h2>火が、川の働きをしていた。</h2>
            <p>
              希少な植物が生育する草原は、過去には河川の増水による攪乱によって維持されてきました。増水によって河川敷の植物が流されたり、土砂に埋まったりする攪乱によって裸地ができると植物が侵入し始め、草原へと移り変わっていきます。このような移り変わりを遷移と呼びますが、草原まで遷移が進んだ場所に現在の希少植物の本来の生育地がありました。土地が安定しているとさらに遷移が進んで樹林になりますが、このような場所では草原性の植物は生きていけません。過去には河川が流路を変えながら増水を繰り返し、遷移が進んだ場所を裸地に戻すことによって草原を維持してきました。この川の働きの代わりを火入れによって行っているのです。
              <br />
              本学部で行われた自然地理学的研究によって、大麻生公園周辺でも過去には大きな流路変更があったことや、約50年前の川砂利採取をきっかけとして流路が固定されてしまったことなどが具体的に明らかにされています。例えば荒川本流は明治時代には現在より約500mも北側を流れていました。それに加えて上流に大きなダムが作られて流量をコントロールできるようになったため、現在の希少植物の生育地には遷移を元に戻すような増水の攪乱が及びにくくなっています。この川の働きの代わりを火入れによって行っているのです。
              <br />
              学部の授業ではCO<sub>2</sub>
              の放出について学びました。熱帯雨林を燃やすと数十年，数百年かけて蓄積した炭素を一気に放出するのに対し、毎年火入れをする草原では1年間に蓄積した分を放出するだけなので、長期的にはCO
              <sub>2</sub>濃度を増加させないことが分かりました。
            </p>
          </div>
          <div className={cn(s.right, s.top)}>
            <div className={s.paddingTop}>
              <img
                src={getPrefixedPath('/assets/images/pc/v4/p-half-kan-b5.jpg')}
              />
              <p className={cn(s.caption)}>
                2019年台風19号による増水で撹乱を受けた荒川河川敷
              </p>
              <img
                src={getPrefixedPath('/assets/images/pc/v4/p-half-kan-b4.jpg')}
              />
              <p className={s.caption}>防火帯</p>
            </div>
          </div>
        </div>

        <Shift
          className={s.shiftEs2}
          startOffset={0}
          layer={[
            <div className={s.layer1}>
              <ShiftLayerLargeImage className={s.pc} />
              <ShiftLayerLargeImageSp className={s.sp} />
            </div>,
            <div className={s.layer2}>
              <figure>
                <picture>
                  <source
                    media="(min-width: 720px)"
                    srcSet={getPrefixedPath('/assets/images/pc/p-pc-k3-2.jpg')}
                  />
                  <img
                    src={getPrefixedPath('/assets/images/sp/p-sp-k3-2.jpg')}
                  />
                </picture>
              </figure>
            </div>,
          ]}
        />

        <div className={s.flex}>
          <div className={s.left}>
            <div className={s.step}>step4</div>
            <h2>
              一筋縄ではいかないから、
              <br />
              地球環境は面白い。
            </h2>
            <p>
              人間は安全で豊かな暮らしをするために砂利採取やダム建設といった活動をすることにより、希少植物の生育場所を奪ってしまいました。一方で火入れという人間の働きかけによって、何とか希少植物を守っているのです。
              <br />
              火入れは、希少草本の生育地である草原を維持し、外来樹木のハリエンジュ林の拡大を防ぐためには効果があります。一方、同じ外来樹木のニワウルシには有利に働き、急速に拡大させる恐れがあることが分かりました。ニワウルシを減らすためには実施が難しい夏の火入れを考える必要があります。
              <br />
              希少植物の多くは火入れによって背は低くなるものの、花を多く咲かせたり、タネを多く作ったりすることによって子孫を増やしやすくなることが分かりました。しかし、毎年火入れをした方がよいイヌハギなどと、数年に1度の火入れの方がよいカワラナデシコのような違いがあることもわかりました。
              <br />
              新たな問題として外来植物のシナダレスズメガヤの増加があります。多くの河川敷で増加していますが、火入れによっては駆除できず、逆に競争相手が減ることによって急激に増える恐れがあります。シナダレスズメガヤに対しては、掘り取りなどの人海戦術が必要になりそうです。（米林仲研究室）
            </p>
          </div>
          <div className={cn(s.right, s.top)}>
            <div className={s.paddingTop}>
              {/* <Tilt direction="left"> */}
              <img
                src={getPrefixedPath('/assets/images/pc/v4/p-half-kan-b6.jpg')}
              />
              {/* </Tilt> */}
              <p className={s.caption}>
                火を入れた場所（赤）と、入れなかった場所（青）におけるイヌハギの芽生えの生き残り数の変化。火を入れた場所では芽生えた数も、秋まで生き残った数も多かった。
              </p>
            </div>
          </div>
        </div>

        <div className={cn(s.icons, s.flex)}>
          <DeptIcon1 className={cn(s.icon1)} />
          <DeptIcon2 className={cn(s.icon2)} />
        </div>

        <div className={s.link}>
          <p>
            上記の内容はあくまでも一例です。環境システム学科は、地球環境や自然環境に「生物・地球」「気象・水文」の領域からアプローチし、創造的な視点で向き合うことができます。もっと詳しく知りたい方は、オープンキャンパスや学科サイト、デジタルパンフレット、LINEからどうぞ。
          </p>
          <a href="#learn-more">
            もっと知る <Arrow />
          </a>
        </div>
      </div>
    </section>
  </>
)

export default Page
